import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, CheckIcon, ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';

// Default approved domains seeded when enabling the filter on a blank session.
const DEFAULT_APPROVED_DOMAINS = [
  '4based.com','6godm.com','a.me','abichungus.straw.page','addictedtoana.com',
  'alejandhra.live','alisawaifu.mydeck.bio','allmylinks.com','allofmysocial.com',
  'amirahhallmylinks.com','anikauwu.com','aozorababe.mydeck.bio','app.fanfix.io',
  'ariegold.carrd.co','astasiadream.com','atom.bio','attpad.com','audreybby.mydeck.bio',
  'ax0bby.me','bare.link','beacons.ai','beacons.page','bellapoarch.ffm.to','biio.fr',
  'bio.link','bio.site','bioallmylinks.com','bouncy.ai','chaturbate.com',
  'checkmy.social','clickformylinks.com','clicklo.co','clickylo.co','clickymylinky.com',
  'cliickto.me','cmplx.it','countmisha.carrd.co','danisthings.com','direct.me',
  'domme.link','ebminisite.app','envy.bio','epuni.carrd.co','f2f.com','fanme.link',
  'fansly.com','fanstories.com','fanvue.com','gaml.ai','gaml.app','gaml.io',
  'getcloserhq.com','getmysocial.com','gherbo.lnk.to','gmscl.com','go.thatsmylink.me',
  'goddesslilarose.carrd.co','heyallmylinks.com','heyliiink.com','heylink.me',
  'hiddengem.bio','hitmypages.com','honeywhisper.online','hoo.be','hubzter.pro',
  'imyujia.me','itschloebellee.creatorcard.bio','itskaitk.com','itskrystal.com',
  'ivy.creatorcard.bio','jubbilix.com','juicy.bio','kaitin.me','kendelkay.me',
  'lasciviouschristmasinfection.com','lattepr1ncess.carrd.co','letschat.bio',
  'liinks.co','link.boo','link.me','linkbio.co','linkcloud.ai','linkdm.me',
  'linkdrop.uk','linkforfans.net','linkgate.bio','linkhaven.net','linkin.gg',
  'links.me','linktr.ee','linktree.com','lnk.bio','loyalfans.com','luchidelmar.bio',
  'luvurfavgirle.creatorcard.bio','luxe.bio','luxx.fans','manyvids.com',
  'mazi.mydeck.bio','meallmylinks.com','mesam29.creatorapp.club','msha.ke',
  'muffins.creatorcard.bio','my.hoo.be','my.piclink.me','myallsocials.com',
  'myaslink.com','mybirds.org','myfanz.link','myliinks.com','mym.fans','myslink.app',
  'naominakamura.bio','ninj.link','onetap.me','onlyfans.com','onlylink.app',
  'onlylinks.com','oopsie.bio','paa.ge','passes.com','patreon.com','paylume.fans',
  'petalspulse.creatorcard.bio','piclink.me','pillar.io','poshbunny.bio',
  'possessrots.carrd.co','pressallmylinks.com','privacy.com.br','rnk.bio',
  'santandave.lnk.to','sarameikasai.carrd.co','seemysocials.com','sent.bio',
  'sfamurri.me','sharingsocials.com','showcontent.online','slt.bio','sml.lnk.to',
  'snipfeed.ai','solo.to','sp-link.link','spicyarialinks.com','spoilerica.taplink.bio',
  'subs.com','supalink.ai','sweetsocials.co','swiftclick.app','switchlinks.ai',
  'tap2link.me','tapallmylinks.com','tapforallmylinks.com','taplink.cc',
  'tapmysocials.com','taptoenter.vip','tecito.app','tlink.me','tr.ee','unlockt.me',
  'vergelink.co','viplink.app','visitmy.social','xbio.ai','xli.ink','yourmiasky.com',
];

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

export default function ScraperV2LinkDomainsTab({ sessionId }) {
  const [domains, setDomains] = useState([]);
  const [approvedDomains, setApprovedDomains] = useState([]);
  const [filterEnabled, setFilterEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [domainTab, setDomainTab] = useState('all'); // 'all' | 'approved' | 'pending'
  const [globalSaving, setGlobalSaving] = useState(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await api.get(`/scraper-v2/sessions/${sessionId}/link-domains`);
      let approved = res.data.approvedDomains || [];

      // Auto-seed the 215 default domains if none are approved yet
      if (approved.length === 0) {
        try {
          const seedRes = await api.post(`/scraper-v2/sessions/${sessionId}/link-domains/approve`, {
            domains: DEFAULT_APPROVED_DOMAINS,
          });
          approved = seedRes.data.approvedDomains || approved;
        } catch {
          // non-fatal — just show empty list
        }
      }

      setDomains(res.data.domains || []);
      setApprovedDomains(approved);
      setFilterEnabled(res.data.filterEnabled || false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load link domains');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
    setDomainTab('all');
    setSearch('');
    setSelected(new Set());
  }, [load]);

  const filtered = useMemo(() => {
    let rows = domains.filter((d) => d.domain); // exclude the null-domain (no-link) entry from table
    if (domainTab === 'approved') rows = rows.filter((d) => approvedDomains.includes(d.domain));
    else if (domainTab === 'pending') rows = rows.filter((d) => !approvedDomains.includes(d.domain));
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((d) => d.domain.includes(q));
    return rows;
  }, [domains, approvedDomains, domainTab, search]);

  const visibleDomainNames = useMemo(
    () => new Set(filtered.filter((d) => d.domain).map((d) => d.domain)),
    [filtered]
  );

  const allVisibleSelected = visibleDomainNames.size > 0
    && [...visibleDomainNames].every((d) => selected.has(d));

  const toggleAll = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const d of visibleDomainNames) {
        if (checked) next.add(d);
        else next.delete(d);
      }
      return next;
    });
  };

  const toggleRow = (domain) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  const selectedApproved = [...selected].filter((d) => approvedDomains.includes(d));
  const selectedUnapproved = [...selected].filter((d) => !approvedDomains.includes(d));

  const handleApprove = async (domainsToApprove) => {
    if (!domainsToApprove.length) return;
    setSaving(true);
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/link-domains/approve`, {
        domains: domainsToApprove,
      });
      setApprovedDomains(res.data.approvedDomains || []);
      setSelected(new Set());
      toast.success(`Approved ${domainsToApprove.length} domain(s)`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve domains');
    } finally {
      setSaving(false);
    }
  };

  const handleUnapprove = async (domainsToUnapprove) => {
    if (!domainsToUnapprove.length) return;
    setSaving(true);
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/link-domains/unapprove`, {
        domains: domainsToUnapprove,
      });
      setApprovedDomains(res.data.approvedDomains || []);
      setSelected(new Set());
      toast.success(`Unapproved ${domainsToUnapprove.length} domain(s)`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to unapprove domains');
    } finally {
      setSaving(false);
    }
  };

  const handleEnableFilter = async () => {
    setSaving(true);
    try {
      // Seed with defaults if empty
      const domainsToSeed = approvedDomains.length === 0 ? DEFAULT_APPROVED_DOMAINS : [];
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/link-domains/approve`, {
        domains: domainsToSeed,
        enable: true,
      });
      setApprovedDomains(res.data.approvedDomains || []);
      setFilterEnabled(true);
      toast.success('Domain filter enabled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to enable filter');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableFilter = async () => {
    setSaving(true);
    try {
      await api.post(`/scraper-v2/sessions/${sessionId}/link-domains/toggle-filter`, { enabled: false });
      setFilterEnabled(false);
      toast.success('Domain filter disabled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to disable filter');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGlobal = async () => {
    setGlobalSaving(true);
    try {
      await api.post('/scraper-v2/global-config/link-domains', {
        domains: approvedDomains,
        filterDefault: filterEnabled,
      });
      toast.success(`Global default saved — ${approvedDomains.length} approved domains · all new sessions will inherit this list`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save global default');
    } finally {
      setGlobalSaving(false);
    }
  };

  const handleEnforce = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/link-domains/enforce`);
      toast.success(`Enforced — ${res.data.updated || 0} cursor(s) updated`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to enforce');
    } finally {
      setSaving(false);
    }
  };

  const namedDomains = domains.filter((d) => d.domain);
  const approvedCount = namedDomains.filter((d) => approvedDomains.includes(d.domain)).length;
  const pendingCount  = namedDomains.length - approvedCount;
  const totalDomains  = namedDomains.length;

  if (!sessionId) {
    return (
      <div className="text-sm text-gray-500 py-8 text-center">
        Select a session to manage link domain approvals.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-2 text-sm">
          <span className="text-slate-500 dark:text-slate-400">Domains: </span>
          <span className="font-semibold">{fmt(totalDomains)}</span>
        </div>
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-2 text-sm">
          <span className="text-emerald-600 dark:text-emerald-400">Approved: </span>
          <span className="font-semibold text-emerald-700 dark:text-emerald-300">{fmt(approvedCount)}</span>
        </div>
        <div className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
          filterEnabled
            ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 text-gray-500'
        }`}>
          Filter: {filterEnabled ? 'ON' : 'OFF'}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter controls */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 p-4 flex flex-wrap items-center gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Domain filter</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            When enabled, FYP auto-sourcing skips accounts whose first link is not from an approved domain.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {filterEnabled ? (
            <button
              type="button"
              disabled={saving}
              onClick={handleDisableFilter}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <ShieldExclamationIcon className="w-4 h-4 text-amber-500" />
              Disable filter
            </button>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={handleEnableFilter}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-50"
            >
              <ShieldCheckIcon className="w-4 h-4" />
              Enable filter
            </button>
          )}
          <button
            type="button"
            disabled={saving || !filterEnabled}
            onClick={handleEnforce}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-400 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-50"
          >
            Enforce now
          </button>
          <button
            type="button"
            disabled={globalSaving || approvedDomains.length === 0}
            onClick={handleSaveGlobal}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-400 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50"
            title="Save current approved list as default for all new scrape sessions"
          >
            {globalSaving ? 'Saving…' : `Save as global default (${approvedDomains.length})`}
          </button>
        </div>
      </div>

      {/* Action bar (shown when rows selected) */}
      {selected.size > 0 && (
        <div className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/60 px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-indigo-700 dark:text-indigo-300 font-medium">
            {selected.size} domain{selected.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2 ml-auto">
            {selectedUnapproved.length > 0 && (
              <button
                type="button"
                disabled={saving}
                onClick={() => handleApprove(selectedUnapproved)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckIcon className="w-3.5 h-3.5" />
                Approve selected ({selectedUnapproved.length})
              </button>
            )}
            {selectedApproved.length > 0 && (
              <button
                type="button"
                disabled={saving}
                onClick={() => handleUnapprove(selectedApproved)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                Unapprove selected ({selectedApproved.length})
              </button>
            )}
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 text-xs hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Search + table */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-3 pt-3 pb-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30">
          {/* Tab filters */}
          <div className="flex gap-1 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-white dark:bg-gray-900 text-sm">
            {[
              { key: 'all',      label: 'All',      count: totalDomains },
              { key: 'approved', label: 'Approved', count: approvedCount },
              { key: 'pending',  label: 'Pending',  count: pendingCount },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                type="button"
                onClick={() => { setDomainTab(key); setSelected(new Set()); }}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  domainTab === key
                    ? key === 'approved'
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                      : key === 'pending'
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                        : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {label} <span className="ml-1 opacity-70">{count}</span>
              </button>
            ))}
          </div>
          {/* Search */}
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search domain…"
            className="ml-auto text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => toggleAll(e.target.checked)}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3 text-right">Accounts</th>
                <th className="px-4 py-3 text-center">Approved</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">Loading…</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    {search ? 'No domains match your search.' : 'No source accounts with profileUrls yet.'}
                  </td>
                </tr>
              )}
              {!loading && filtered.map((row) => {
                const isApproved = row.domain ? approvedDomains.includes(row.domain) : false;
                const isSelected = row.domain ? selected.has(row.domain) : false;
                return (
                  <tr
                    key={row.domain ?? '__nolink__'}
                    onClick={() => row.domain && toggleRow(row.domain)}
                    className={`hover:bg-gray-50/80 dark:hover:bg-gray-800/40 ${row.domain ? 'cursor-pointer' : ''} ${
                      isSelected ? 'bg-indigo-50/60 dark:bg-indigo-900/10' : ''
                    }`}
                  >
                    <td className="px-4 py-2.5">
                      {row.domain && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(row.domain)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded"
                        />
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {row.domain ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-800 dark:text-gray-200">
                            {row.domain}
                          </span>
                          {row.isDirectPlatform && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                              platform
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="italic text-gray-400 text-xs">no link</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600 dark:text-gray-300">
                      {fmt(row.count)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {row.domain && (
                        isApproved ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                            <CheckIcon className="w-3 h-3" />
                            approved
                          </span>
                        ) : (
                          <span className="text-[11px] text-gray-400 dark:text-gray-500">pending</span>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
